/**
 * useEnabledModels — shared SWR hook for the per-tenant enabled-models registry.
 *
 * Backs:
 *   - Costs page checkbox state (CostsPanel)
 *   - "Configure available models →" CTA on Models & Providers (ProvidersTab)
 *   - Routing tab option filtering + mismatched-routing warning (RoutingTab)
 *   - Chat picker model list (when that surface lands in seitiate-plus)
 *
 * Part of: MODEL-REGISTRY-PER-TENANT-1 (dispatched 2026-05-16).
 */

import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import {
  getEnabledModels,
  setEnabledModels,
  type EnabledModelEntry,
  type EnabledModelsResponse,
} from '@/api/llmSettings';

export const ENABLED_MODELS_SWR_KEY = '/api/v1/settings/llm/enabled-models';

function normalizeModelId(modelId: string): string {
  return modelId
    .trim()
    .toLowerCase()
    .replace(/[\s_.:]+/g, '-')
    .replace(/-latest$/, '')
    .replace(/-001$/, '');
}

function sameEnabledModel(entry: EnabledModelEntry, providerId: string, modelId: string): boolean {
  return (
    entry.provider_id === providerId &&
    normalizeModelId(entry.model_id) === normalizeModelId(modelId)
  );
}

export interface UseEnabledModelsResult {
  /** The persisted set of (provider_id, model_id) pairs. Empty array if not yet loaded. */
  enabled: EnabledModelEntry[];
  /** Fast membership check — `true` if this (provider, model) is currently enabled. */
  isEnabled: (providerId: string, modelId: string) => boolean;
  /** Counter for the Costs page header ("N enabled"). */
  count: number;
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /** Non-null when the fetch errored. */
  error: Error | undefined;
  /** Toggle a single pair on/off. Optimistic update + revalidation. */
  toggle: (providerId: string, modelId: string) => Promise<void>;
  /** Replace the full set (used by callers that batch). */
  replace: (entries: EnabledModelEntry[]) => Promise<EnabledModelsResponse>;
  /** Force a re-fetch (after key add/remove on the Providers tab, etc.). */
  revalidate: () => Promise<EnabledModelsResponse | undefined>;
}

export function useEnabledModels(): UseEnabledModelsResult {
  const { data, error, isLoading, mutate } = useSWR<EnabledModelsResponse>(
    ENABLED_MODELS_SWR_KEY,
    () => getEnabledModels(),
    {
      // Mid-traffic page — keep dedupe window short so checkbox toggles see
      // each other quickly across tabs, but don't hammer the engine.
      dedupingInterval: 2_000,
    },
  );

  const enabled = useMemo(() => data?.enabled ?? [], [data?.enabled]);

  const isEnabled = useCallback(
    (providerId: string, modelId: string) =>
      enabled.some((e) => sameEnabledModel(e, providerId, modelId)),
    [enabled],
  );

  const toggle = useCallback(
    async (providerId: string, modelId: string) => {
      const exists = enabled.some((e) => sameEnabledModel(e, providerId, modelId));
      const next: EnabledModelEntry[] = exists
        ? enabled.filter(
            (e) => !sameEnabledModel(e, providerId, modelId),
          )
        : [...enabled, { provider_id: providerId, model_id: modelId }];

      await mutate(
        async () => {
          const response = await setEnabledModels(next);
          return response;
        },
        {
          optimisticData: { enabled: next },
          rollbackOnError: true,
          revalidate: false,
        },
      );
    },
    [enabled, mutate],
  );

  const replace = useCallback(
    async (entries: EnabledModelEntry[]) => {
      const response = await setEnabledModels(entries);
      await mutate(response, { revalidate: false });
      return response;
    },
    [mutate],
  );

  const revalidate = useCallback(() => mutate(), [mutate]);

  return {
    enabled,
    isEnabled,
    count: enabled.length,
    isLoading,
    error: error as Error | undefined,
    toggle,
    replace,
    revalidate,
  };
}
